import { useEffect, useCallback, useState } from 'react'

export interface MultiSelectDiffObject<T> {
  added: Array<T>
  removed: Array<T>
  initial: Array<T>
  current: Array<T>
}

interface MultiSelectDiffArgs<T, K> {
  initialSelection: Array<T>
  key: K
}

interface MulitSelectValue<T> {
  getMultiSelectDiff: () => MultiSelectDiffObject<T>
  onSelect: (item: T) => 'add' | 'remove'
  selectedItems: Array<T>
}

// declare outside of function so that its referentially stable across rerenders
const DEFAULT_INITIAL_SELECTION: Array<unknown> = []

export function useMultiSelectDiff<T, K extends keyof T>({
  initialSelection = DEFAULT_INITIAL_SELECTION as Array<T>,
  key,
}: MultiSelectDiffArgs<T, K>): MulitSelectValue<T> {
  const [initialSelectedItems, setInitialSelectedItems] =
    useState(initialSelection)
  const [selectedItems, setSelectedItems] = useState(initialSelection)

  // update initial selection value in case selected list can
  // be edited outside of main selection component (e.g. CollabortorPermissionList)
  useEffect(() => {
    if (initialSelection.length !== initialSelectedItems.length) {
      setInitialSelectedItems(initialSelection)
      setSelectedItems(initialSelection)
    } else {
      const currentIds = initialSelectedItems.map((item) => item[key]).join('')
      const newIds = initialSelection.map((item) => item[key]).join('')
      if (newIds !== currentIds) {
        setInitialSelectedItems(initialSelection)
        setSelectedItems(initialSelection)
      }
    }
  }, [initialSelection, initialSelectedItems, key])

  // handle case where selectable objects may be different from selected objects
  // e.g. board collaborators selects Users but renders selected value as Collaborators
  const isSelected = useCallback(
    (option: T, selectionArray: Array<T>) => {
      if (selectionArray.includes(option)) return true

      const selectedIds = selectionArray.map((item) => item[key])
      return selectedIds.includes(option[key])
    },
    [key]
  )

  const onSelect = useCallback(
    (selectedItem: T) => {
      if (isSelected(selectedItem, selectedItems)) {
        setSelectedItems((prevSelectedItems) =>
          prevSelectedItems.filter((item) => item[key] !== selectedItem[key])
        )
        return 'remove'
      }

      setSelectedItems((prevSelectedItems) => [
        ...prevSelectedItems,
        selectedItem,
      ])
      return 'add'
    },
    [selectedItems, isSelected, key]
  )

  const getMultiSelectDiff = useCallback(() => {
    if (!initialSelectedItems.length) {
      return {
        added: selectedItems,
        removed: [],
        initial: initialSelectedItems,
        current: selectedItems,
      }
    }

    return {
      added: selectedItems.filter(
        (item) => !isSelected(item, initialSelectedItems)
      ),
      removed: initialSelectedItems.filter(
        (item) => !isSelected(item, selectedItems)
      ),
      initial: initialSelectedItems,
      current: selectedItems,
    }
  }, [selectedItems, initialSelectedItems, isSelected])

  return {
    getMultiSelectDiff,
    onSelect,
    selectedItems,
  }
}
